import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { OrderModel } from '../model/orderModel';

interface DailyDashboardProps {
  branchId: string;
}

interface DPMap {
  [uid: string]: string; // uid → storename
}

interface RecentUser {
  id: string;
  phone: string;
  username?: string | null;
  walletBalance: number;
  notactive: boolean;
  createdTimestamp: any;
}

const STATUS_COLORS: Record<string, string> = {
  DELIVERED: '#10b981',
  CANCELLED: '#ef4444',
  PENDING: '#f59e0b',
  ACCEPTED: '#3b82f6',
  PICKED: '#8b5cf6',
};

function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateRange(d: Date) {
  const start = new Date(d);
  start.setHours(0, 0, 0, 0);
  const end = new Date(d);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function formatTime(ts: any): string {
  if (!ts) return '—';
  const d: Date =
    ts && typeof ts === 'object' && 'toDate' in ts
      ? ts.toDate()
      : new Date(typeof ts === 'number' ? ts : ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const DailyDashboard: React.FC<DailyDashboardProps> = ({ branchId }) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 650;

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [orders, setOrders] = useState<OrderModel[]>([]);
  const [dpMap, setDpMap] = useState<DPMap>({});
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const isToday = toLocalDateString(selectedDate) === toLocalDateString(new Date());

  // Live recent users (latest 15)
  useEffect(() => {
    const q = query(
      collection(db, 'users'),
      orderBy('createdTimestamp', 'desc'),
      limit(15)
    );
    const unsub = onSnapshot(q, (snap) => {
      setRecentUsers(
        snap.docs.map((d) => ({ id: d.id, ...d.data() } as RecentUser))
      );
    });
    return () => unsub();
  }, []);

  // Load delivery partner names once
  useEffect(() => {
    getDocs(collection(db, 'zapee_delivery_partners')).then((snap) => {
      const map: DPMap = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        const label: string = data.name || data.storename || data.phone || data.email || d.id;
        // key by every plausible id so an order's deliveryPartnerId resolves
        [data.partnerId, data.uid, data.storeuid, data.pincode, d.id].forEach((k: any) => {
          if (k) map[String(k)] = label;
        });
      });
      setDpMap(map);
    });
  }, []);

  // Orders for selected date filtered by branchId (live only for today)
  useEffect(() => {
    if (!branchId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { start, end } = dateRange(selectedDate);
    const q = query(
      collection(db, 'zapee_restaurant_orders'),
      where('branchId', '==', branchId),
      where('createdAt', '>=', Timestamp.fromDate(start)),
      where('createdAt', '<=', Timestamp.fromDate(end)),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() } as OrderModel));
      setOrders(data);
      setLastUpdated(new Date());
      setLoading(false);
    });

    return () => unsub();
  }, [branchId, selectedDate]);

  // ── Computed stats ───────────────────────────────────────────────
  const delivered  = orders.filter((o) => o.status === 'DELIVERED');
  const cancelled  = orders.filter((o) => ['CANCELLED', 'CANCEL'].includes(o.status || ''));
  const pending    = orders.filter((o) => o.status === 'PENDING');
  const confirmed  = orders.filter((o) => o.status === 'CONFIRMED');
  const preparing  = orders.filter((o) => ['PROCESSING', 'ACCEPTED', 'PICKED', 'PREPARING'].includes(o.status || ''));
  const assigned   = orders.filter((o) => !!o.deliveryPartnerId);

  const knownStatuses = new Set(['DELIVERED', 'CANCELLED', 'CANCEL', 'PENDING', 'CONFIRMED', 'PROCESSING', 'ACCEPTED', 'PICKED', 'PREPARING']);
  const others     = orders.filter((o) => !knownStatuses.has(o.status || ''));

  const totalProductAmt  = delivered.reduce((s, o) => s + (o.totalPrice || 0), 0);
  const totalDelivery    = delivered.reduce((s, o) => s + (o.charges?.deliveryCharge || 0), 0);
  const totalPacking     = delivered.reduce((s, o) => s + (o.charges?.packing || 0), 0);

  // ── Sub-components ───────────────────────────────────────────────
  const StatCard = ({
    label,
    value,
    accent,
    sub,
  }: {
    label: string;
    value: string | number;
    accent: string;
    sub?: string;
  }) => (
    <View style={[styles.statCard, { borderTopColor: accent }]}>
      <Text style={styles.statLabel}>{label}</Text>
      <Text style={[styles.statValue, { color: accent }]}>{value}</Text>
      {sub ? <Text style={styles.statSub}>{sub}</Text> : null}
    </View>
  );

  const StatusBadge = ({ status }: { status: string }) => (
    <View style={[styles.badge, { backgroundColor: (STATUS_COLORS[status] ?? '#94a3b8') + '20' }]}>
      <Text style={[styles.badgeText, { color: STATUS_COLORS[status] ?? '#94a3b8' }]}>
        {status}
      </Text>
    </View>
  );

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#21808D" />
        <Text style={styles.loadingText}>Loading today's data…</Text>
      </View>
    );
  }

  if (!branchId) {
    return (
      <View style={styles.centered}>
        <Text style={styles.emptyText}>No branch assigned to your account.</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.pageTitle}>
            {isToday ? "Today's Overview" : 'Orders Overview'}
          </Text>
          <Text style={styles.pageSubtitle}>
            {selectedDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Text>
        </View>

        <View style={styles.headerRight}>
          {/* Prev day */}
          <TouchableOpacity
            style={styles.navBtn}
            onPress={() => setSelectedDate(d => { const p = new Date(d); p.setDate(p.getDate() - 1); return p; })}
          >
            <Text style={styles.navBtnText}>‹</Text>
          </TouchableOpacity>

          {/* Date input */}
          <input
            type="date"
            value={toLocalDateString(selectedDate)}
            max={toLocalDateString(new Date())}
            onChange={(e: any) => {
              if (e.target.value) setSelectedDate(new Date(e.target.value + 'T00:00:00'));
            }}
            style={dateInputStyle}
          />

          {/* Next day — disabled if today */}
          <TouchableOpacity
            style={[styles.navBtn, isToday && styles.navBtnDisabled]}
            onPress={() => {
              if (!isToday)
                setSelectedDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; });
            }}
            disabled={isToday}
          >
            <Text style={[styles.navBtnText, isToday && styles.navBtnTextDisabled]}>›</Text>
          </TouchableOpacity>

          {/* Today shortcut */}
          {!isToday && (
            <TouchableOpacity style={styles.todayBtn} onPress={() => setSelectedDate(new Date())}>
              <Text style={styles.todayBtnText}>Today</Text>
            </TouchableOpacity>
          )}

          {isToday && (
            <View style={styles.liveIndicator}>
              <View style={styles.liveDot} />
              <Text style={styles.liveText}>Live</Text>
            </View>
          )}
        </View>
      </View>

      {/* ── Stats Row 1: Status counts ── */}
      <View style={styles.statsGrid}>
        <StatCard label="Total"     value={orders.length}       accent="#21808D" />
        <StatCard label="Delivered" value={delivered.length}    accent="#10b981" />
        <StatCard label="Cancelled" value={cancelled.length}    accent="#ef4444" />
        <StatCard label="Pending"   value={pending.length}      accent="#f59e0b" />
        <StatCard label="Confirmed" value={confirmed.length}    accent="#3b82f6" />
        <StatCard label="Preparing" value={preparing.length}    accent="#8b5cf6" />
        <StatCard label="Others"    value={others.length}       accent="#94a3b8" />
      </View>

      {/* ── Stats Row 2: Amount cards ── */}
      <View style={styles.statsGrid}>
        <StatCard label="Prod Amt"  value={`₹${totalProductAmt.toFixed(0)}`} accent="#10b981" sub="Del. only" />
        <StatCard label="Del. Chrg" value={`₹${totalDelivery.toFixed(0)}`}   accent="#8b5cf6" sub="Del. only" />
        <StatCard label="Packing"   value={`₹${totalPacking.toFixed(0)}`}    accent="#f59e0b" sub="Del. only" />
        <StatCard label="Total Rev" value={`₹${(totalProductAmt + totalDelivery + totalPacking).toFixed(0)}`} accent="#21808D" sub="Del. only" />
      </View>

      {/* ── Recent Orders + Assigned DP — side by side on desktop ── */}
      <View style={[styles.tablesRow, isMobile && styles.tablesRowMobile]}>

        {/* Recent Orders · Live Status */}
        <View style={[styles.section, styles.tablePanel]}>
          <Text style={styles.sectionTitle}>Recent Orders · Live Status</Text>
          {orders.length === 0 ? (
            <View style={styles.emptyBox}>
              <Text style={styles.emptyText}>No orders today yet.</Text>
            </View>
          ) : (
            <>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.cell, styles.cellOrder, styles.headerText]}>Order #</Text>
                <Text style={[styles.cell, styles.cellTime,  styles.headerText]}>Time</Text>
                <Text style={[styles.cell, styles.cellAmt,   styles.headerText]}>Amount</Text>
                <Text style={[styles.cell, styles.cellStatus,styles.headerText]}>Status</Text>
              </View>
              {orders.slice(0, 30).map((order) => (
                <View key={order.id} style={styles.tableRow}>
                  <Text style={[styles.cell, styles.cellOrder]} numberOfLines={1}>
                    {Array.isArray(order.orderNumber) ? order.orderNumber.join('-') : order.orderNumber}
                  </Text>
                  <Text style={[styles.cell, styles.cellTime]}>{formatTime(order.createdAt)}</Text>
                  <Text style={[styles.cell, styles.cellAmt]}>₹{(order.totalPrice || 0).toFixed(0)}</Text>
                  <View style={[styles.cell, styles.cellStatus]}>
                    <StatusBadge status={order.status || 'PENDING'} />
                  </View>
                </View>
              ))}
            </>
          )}
        </View>

        {/* Assigned DP Orders */}
        <View style={[styles.section, styles.tablePanel]}>
          <Text style={styles.sectionTitle}>Assigned Delivery Partner Orders</Text>
          {assigned.length === 0 ? (
            <View style={styles.emptyBox}>
              <Text style={styles.emptyText}>No orders assigned to a delivery partner today.</Text>
            </View>
          ) : (
            <>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.cell, styles.cellOrder,  styles.headerText]}>Order #</Text>
                <Text style={[styles.cell, styles.cellPartner,styles.headerText]}>Partner</Text>
                <Text style={[styles.cell, styles.cellAmt,    styles.headerText]}>Amount</Text>
                <Text style={[styles.cell, styles.cellStatus, styles.headerText]}>Status</Text>
              </View>
              {assigned.slice(0, 30).map((order) => (
                <View key={order.id} style={styles.tableRow}>
                  <Text style={[styles.cell, styles.cellOrder]} numberOfLines={1}>
                    {Array.isArray(order.orderNumber) ? order.orderNumber.join('-') : order.orderNumber}
                  </Text>
                  <Text style={[styles.cell, styles.cellPartner]} numberOfLines={1}>
                    {dpMap[order.deliveryPartnerId!] || order.deliveryPartnerId}
                  </Text>
                  <Text style={[styles.cell, styles.cellAmt]}>₹{(order.totalPrice || 0).toFixed(0)}</Text>
                  <View style={[styles.cell, styles.cellStatus]}>
                    <StatusBadge status={order.status || 'PENDING'} />
                  </View>
                </View>
              ))}
            </>
          )}
        </View>

      </View>

      {/* ── Recent Users ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recent Users</Text>
        {recentUsers.length === 0 ? (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyText}>No users yet.</Text>
          </View>
        ) : (
          <>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.cell, styles.cellUser,   styles.headerText]}>Name / Phone</Text>
              <Text style={[styles.cell, styles.cellWallet, styles.headerText]}>Wallet</Text>
              <Text style={[styles.cell, styles.cellJoined, styles.headerText]}>Joined</Text>
              <Text style={[styles.cell, styles.cellStatus, styles.headerText]}>Status</Text>
            </View>
            {recentUsers.map((u) => (
              <View key={u.id} style={styles.tableRow}>
                <View style={[styles.cell, styles.cellUser]}>
                  <Text style={styles.userName} numberOfLines={1}>{u.username || '—'}</Text>
                  <Text style={styles.userPhone}>{u.phone || '—'}</Text>
                </View>
                <Text style={[styles.cell, styles.cellWallet]}>₹{(u.walletBalance || 0).toFixed(0)}</Text>
                <Text style={[styles.cell, styles.cellJoined]}>{formatTime(u.createdTimestamp)}</Text>
                <View style={[styles.cell, styles.cellStatus]}>
                  <View style={[styles.badge, { backgroundColor: u.notactive ? '#fee2e220' : '#d1fae520' }]}>
                    <Text style={[styles.badgeText, { color: u.notactive ? '#ef4444' : '#10b981' }]}>
                      {u.notactive ? 'Inactive' : 'Active'}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </>
        )}
      </View>
    </ScrollView>
  );
};

const dateInputStyle: React.CSSProperties = {
  height: 36,
  paddingLeft: 10,
  paddingRight: 10,
  borderRadius: 8,
  border: '1.5px solid #e2e8f0',
  backgroundColor: '#f8fafc',
  fontSize: 14,
  color: '#1a2332',
  outline: 'none',
  cursor: 'pointer',
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f6f8fb',
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#64748b',
  },
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 8,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1a2332',
  },
  pageSubtitle: {
    fontSize: 13,
    color: '#64748b',
    marginTop: 2,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  navBtn: {
    width: 32,
    height: 36,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  navBtnDisabled: {
    opacity: 0.35,
  },
  navBtnText: {
    fontSize: 20,
    color: '#21808D',
    fontWeight: '700',
    lineHeight: 22,
  },
  navBtnTextDisabled: {
    color: '#94a3b8',
  },
  todayBtn: {
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: '#21808D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  todayBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  liveIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#e6f7f5',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#10b981',
  },
  liveText: {
    fontSize: 12,
    color: '#21808D',
    fontWeight: '600',
  },
  // Stats
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 16,
  },
  statsGridMobile: {},
  statCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingVertical: 7,
    paddingHorizontal: 10,
    flex: 1,
    minWidth: 70,
    borderTopWidth: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 9,
    color: '#64748b',
    fontWeight: '600',
    marginBottom: 3,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  statValue: {
    fontSize: 17,
    fontWeight: '800',
    textAlign: 'center',
  },
  statSub: {
    fontSize: 9,
    color: '#94a3b8',
    marginTop: 2,
    textAlign: 'center',
  },
  // Side-by-side row for the two order tables
  tablesRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 20,
  },
  tablesRowMobile: {
    flexDirection: 'column',
  },
  tablePanel: {
    flex: 1,
    marginBottom: 0, // row container owns the bottom margin
  },
  // Section
  section: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 18,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1a2332',
    marginBottom: 14,
  },
  emptyBox: {
    paddingVertical: 28,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#94a3b8',
  },
  // Table
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  tableHeader: {
    borderBottomWidth: 2,
    borderBottomColor: '#e2e8f0',
    paddingBottom: 8,
    marginBottom: 2,
  },
  headerText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  cell: {
    fontSize: 13,
    color: '#1a2332',
    paddingHorizontal: 4,
  },
  cellOrder: {
    flex: 2,
    fontWeight: '600',
  },
  cellTime: {
    flex: 1,
    color: '#64748b',
  },
  cellAmt: {
    flex: 1,
    fontWeight: '600',
  },
  cellStatus: {
    flex: 1.5,
  },
  cellPartner: {
    flex: 2,
    color: '#475569',
  },
  cellUser: {
    flex: 2.5,
  },
  cellWallet: {
    flex: 1,
    fontWeight: '600',
    color: '#21808D',
  },
  cellJoined: {
    flex: 1.2,
    color: '#64748b',
  },
  userName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1a2332',
  },
  userPhone: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 1,
  },
  // Badge
  badge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
});

export default DailyDashboard;
